import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Paper,
  Chip,
  IconButton,
  Drawer,
  Button,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import { useParams, useSearchParams } from 'react-router-dom';
import Navbar from './common/Navbar';
import Footer from './common/Footer';
import ProductCard from './common/ProductCard';
import Breadcrumb from './common/Breadcrumb';
import Loader from './common/Loader';
import { getProductsByCategory, getAllProducts, getCategories, getCategoryById } from '../services/api';

const Filter = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') || '';
  const categoryId = id || 'All'; // Default to 'All' if no id parameter
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoryName, setCategoryName] = useState('');
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [categoryIdToNameMap, setCategoryIdToNameMap] = useState({});

  // Filter states
  const [sortBy, setSortBy] = useState('relevance');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [inStockOnly, setInStockOnly] = useState(false);
  const [showAllCategories, setShowAllCategories] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [productsRes, categoriesRes] = await Promise.all([
          categoryId === 'All' || searchQuery ? getAllProducts() : getProductsByCategory(categoryId),
          getCategories(),
        ]);

        const productsData = Array.isArray(productsRes.data?.data) ? productsRes.data.data : [];
        setProducts(productsData);
        setFilteredProducts(productsData);
        const categoriesData = Array.isArray(categoriesRes.data) ? categoriesRes.data : [];
        setCategories(categoriesData);

        // Create a mapping from category ID to category name
        const idToNameMap = {};
        categoriesData.forEach((cat) => {
          if (cat._id && cat.CategoryName) {
            idToNameMap[cat._id] = cat.CategoryName;
          }
        });
        setCategoryIdToNameMap(idToNameMap);

        // Set category name based on search or category
        if (searchQuery) {
          setCategoryName(`Search Results for "${searchQuery}"`);
        } else if (categoryId !== 'All') {
          try {
            const catRes = await getCategoryById(categoryId);
            if (catRes.data?.CategoryName) {
              setCategoryName(catRes.data.CategoryName);
            } else {
              setCategoryName(idToNameMap[categoryId] || categoryId);
            }
          } catch {
            setCategoryName(idToNameMap[categoryId] || categoryId);
          }
        } else {
          setCategoryName('All Products');
        }
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [categoryId, searchQuery]);

  // Apply filters and sorting
  useEffect(() => {
    let result = [...products];

    // Search filter - filter by product name
    if (searchQuery.trim()) {
      const query = searchQuery.trim().toLowerCase();
      result = result.filter((p) => {
        const productName = (p.ProductName || '').toLowerCase();
        return productName.includes(query);
      });
    }

    // Category filter
    if (selectedCategories.length > 0) {
      result = result.filter((p) => {
        // ProductCategory can be either an ID string or an object with CategoryName
        let catName;
        if (typeof p.ProductCategory === 'object' && p.ProductCategory?.CategoryName) {
          catName = p.ProductCategory.CategoryName;
        } else if (typeof p.ProductCategory === 'string') {
          // It's an ID, look it up in the map
          catName = categoryIdToNameMap[p.ProductCategory];
        } else {
          catName = p.ProductCategory;
        }
        return catName && selectedCategories.includes(catName);
      });
    }

    // In stock filter
    if (inStockOnly) {
      result = result.filter((p) => p.ProductQuantity > 0);
    }

    // Sorting
    switch (sortBy) {
      case 'price-low':
        result.sort((a, b) => a.ProductPrice - b.ProductPrice);
        break;
      case 'price-high':
        result.sort((a, b) => b.ProductPrice - a.ProductPrice);
        break;
      case 'name-az':
        result.sort((a, b) => a.ProductName.localeCompare(b.ProductName));
        break;
      case 'name-za':
        result.sort((a, b) => b.ProductName.localeCompare(a.ProductName));
        break;
      case 'discount':
        result.sort((a, b) => {
          const discA = ((a.ProductMRP - a.ProductPrice) / a.ProductMRP) * 100;
          const discB = ((b.ProductMRP - b.ProductPrice) / b.ProductMRP) * 100;
          return discB - discA;
        });
        break;
      default:
        break;
    }

    setFilteredProducts(result);
  }, [products, sortBy, selectedCategories, inStockOnly, categoryIdToNameMap, searchQuery]);

  const handleCategoryToggle = (categoryName) => {
    setSelectedCategories((prev) =>
      prev.includes(categoryName)
        ? prev.filter((c) => c !== categoryName)
        : [...prev, categoryName]
    );
  };

  const clearFilters = () => {
    setSortBy('relevance');
    setSelectedCategories([]);
    setInStockOnly(false);
  };

  const FilterSidebar = () => (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" fontWeight="bold">
          Filters
        </Typography>
        <Button size="small" onClick={clearFilters} sx={{ color: '#876513' }}>
          Clear All
        </Button>
      </Box>

      {/* Categories */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
          Categories
        </Typography>
        {(showAllCategories ? categories : categories.slice(0, 5)).map((category) => (
          <FormControlLabel
            key={category._id}
            control={
              <Checkbox
                checked={selectedCategories.includes(category.CategoryName)}
                onChange={() => handleCategoryToggle(category.CategoryName)}
                sx={{ color: '#876513', '&.Mui-checked': { color: '#876513' } }}
              />
            }
            label={category.CategoryName}
            sx={{ display: 'block' }}
          />
        ))}
        {categories.length > 5 && (
          <Button
            size="small"
            onClick={() => setShowAllCategories(!showAllCategories)}
            sx={{
              color: '#876513',
              mt: 1,
              textTransform: 'none',
              '&:hover': { bgcolor: 'rgba(139,69,19,0.05)' },
            }}
          >
            {showAllCategories ? 'See less...' : 'See more...'}
          </Button>
        )}
      </Box>

      {/* In Stock */}
      <Box sx={{ mb: 4 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={inStockOnly}
              onChange={(e) => setInStockOnly(e.target.checked)}
              sx={{ color: '#876513', '&.Mui-checked': { color: '#876513' } }}
            />
          }
          label="In Stock Only"
        />
      </Box>
    </Box>
  );

  if (loading) {
    return <Loader message="Finding products..." />;
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#faf8f5' }}>
      <Navbar />

      <Container maxWidth="lg" sx={{ py: 3 }}>
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: 'Shop', path: '/filter/All' },
            { label: categoryName },
          ]}
        />

        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
            mb: 3,
          }}
        >
          <Box>
            <Typography variant="h4" fontWeight="bold" color="#3E2723">
              {categoryName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {filteredProducts.length} products found
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {/* Sort */}
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                displayEmpty
              >
                <MenuItem value="relevance">Relevance</MenuItem>
                <MenuItem value="price-low">Price: Low to High</MenuItem>
                <MenuItem value="price-high">Price: High to Low</MenuItem>
                <MenuItem value="name-az">Name: A to Z</MenuItem>
                <MenuItem value="name-za">Name: Z to A</MenuItem>
                <MenuItem value="discount">Best Discount</MenuItem>
              </Select>
            </FormControl>

            {/* Mobile Filter Button */}
            {isMobile && (
              <IconButton
                onClick={() => setDrawerOpen(true)}
                sx={{ border: '1px solid #ddd', borderRadius: 2 }}
              >
                <FilterListIcon />
              </IconButton>
            )}
          </Box>
        </Box>

        {/* Active Filters */}
        {selectedCategories.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
            {selectedCategories.map((cat) => (
              <Chip
                key={cat}
                label={cat}
                onDelete={() => handleCategoryToggle(cat)}
                sx={{ bgcolor: '#f5ebe0' }}
              />
            ))}
          </Box>
        )}

        <Grid container spacing={3}>
          {/* Sidebar - Desktop */}
          {!isMobile && (
            <Grid item md={3}>
              <Paper elevation={0} sx={{ borderRadius: 2, bgcolor: '#fff' }}>
                <FilterSidebar />
              </Paper>
            </Grid>
          )}

          {/* Products Grid */}
          <Grid item xs={12} md={isMobile ? 12 : 9}>
            {filteredProducts.length > 0 ? (
              <Grid container spacing={3}>
                {filteredProducts.map((product) => (
                  <Grid
                    item
                    xs={6}
                    sm={6}
                    md={4}
                    key={product._id}
                    sx={{ display: 'flex', justifyContent: 'center' }}
                  >
                    <ProductCard product={product} />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box
                sx={{
                  textAlign: 'center',
                  py: 8,
                  bgcolor: '#fff',
                  borderRadius: 2,
                }}
              >
                <Typography variant="h5" color="text.secondary" gutterBottom>
                  No products found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Try adjusting your filters or search term
                </Typography>
                <Button
                  onClick={clearFilters}
                  sx={{ mt: 2, color: '#876513' }}
                >
                  Clear Filters
                </Button>
              </Box>
            )}
          </Grid>
        </Grid>
      </Container>

      {/* Mobile Filter Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        <Box sx={{ width: 300 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              borderBottom: '1px solid #eee',
            }}
          >
            <Typography variant="h6">Filters</Typography>
            <IconButton onClick={() => setDrawerOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          <FilterSidebar />
        </Box>
      </Drawer>

      <Footer />
    </Box>
  );
};

export default Filter;
